import { useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAdmin?: boolean
  skipOnboardingCheck?: boolean
}

/**
 * Hard redirect that bypasses React Router to create a fresh Supabase
 * client. Used for auth-boundary transitions (→ /login) where SPA
 * navigation would preserve corrupted Web Lock state.
 */
function HardRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.href = to
  }, [to])
  return (
    <div className="min-h-screen bg-riseup-black light:bg-light-bg flex items-center justify-center transition-colors duration-300">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--bn-accent)]"></div>
        <p className="mt-4 text-riseup-gray-400 light:text-gray-600">Redirection...</p>
      </div>
    </div>
  )
}

export default function ProtectedRoute({ children, requireAdmin = false, skipOnboardingCheck = false }: ProtectedRouteProps) {
  const { user, profile, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-riseup-black light:bg-light-bg flex items-center justify-center transition-colors duration-300">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--bn-accent)]"></div>
          <p className="mt-4 text-riseup-gray-400 light:text-gray-600">Chargement...</p>
        </div>
      </div>
    )
  }

  // Hard reload to /login — destroys corrupted Supabase Web Lock state
  if (!user) {
    return <HardRedirect to="/login" />
  }

  if (requireAdmin && profile?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }

  if (!skipOnboardingCheck && profile && !profile.onboarding_completed) {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}
